import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { getDocumentTypeConfig } from '../lib/documentTypes'

// ── Batch-fetch doc counts for many entities in ONE query ────────
// Use this in list/table pages instead of letting each DocButton query alone.
export function useDocCounts(entityIds) {
  const [counts, setCounts] = useState({})

  useEffect(() => {
    const ids = (entityIds || []).filter(Boolean)
    if (ids.length === 0) { setCounts({}); return }

    let cancelled = false
    const BATCH = 500 // stay well under URL/query limits

    async function load() {
      const merged = {}
      for (let i = 0; i < ids.length; i += BATCH) {
        const slice = ids.slice(i, i + BATCH)
        const { data } = await supabase
          .from('documents')
          .select('entity_id')
          .in('entity_id', slice)
        for (const row of (data || [])) {
          merged[row.entity_id] = (merged[row.entity_id] || 0) + 1
        }
      }
      if (!cancelled) setCounts(merged)
    }
    load()
    return () => { cancelled = true }
  }, [JSON.stringify(entityIds || [])])

  return counts
}

// ── Auto-delete docs + storage when entity is deleted ────────────
export async function deleteEntityDocuments(entityId) {
  const { data: docs } = await supabase
    .from('documents')
    .select('file_path')
    .eq('entity_id', entityId)
  if (docs?.length) {
    await supabase.storage.from('documents').remove(docs.map(d => d.file_path))
    await supabase.from('documents').delete().eq('entity_id', entityId)
  }
}

// ── Safe deletion of a record that has attached documents (Legacy #2/#3) ─────────────────────
// Rule: confirm -> delete the DATABASE record -> verify it was really deleted -> only then remove its documents.
// deleteEntityDocuments() above is kept exactly as it was for the modules that still use it.

// Checked removal of every document of an entity, ONE DOCUMENT AT A TIME:
//   1. remove the document's file from storage and require storage to CONFIRM it,
//   2. only then delete THAT document's row and require the database to confirm exactly 1 row,
//   3. if a step fails, keep that document's row (it holds the file path, so the cleanup can be retried safely) and go on with the others.
// Never throws. Returns { ok, found, removed, failed:[{ id, file, step, error }] } (+ step:'list' when the documents could not even be listed).
export async function removeEntityDocuments(entityId) {
  const { data: docs, error: listError } = await supabase
    .from('documents').select('id, file_name, file_path').eq('entity_id', entityId)
  if (listError) return { ok: false, step: 'list', error: listError.message, found: 0, removed: 0, failed: [] }
  if (!docs?.length) return { ok: true, found: 0, removed: 0, failed: [] }

  const failed = []
  let removed = 0
  for (const d of docs) {
    const file = d.file_name || d.file_path
    if (d.file_path) {
      // The storage API answers with the objects it really removed and does NOT raise an error when its access policy
      // refuses a removal (it simply removes nothing). The ONLY proof of success is its own confirmation.
      const { data: gone, error: storageError } = await supabase.storage.from('documents').remove([d.file_path])
      if (storageError) { failed.push({ id: d.id, file, step: 'storage', error: storageError.message }); continue }
      if ((gone || []).length < 1) { failed.push({ id: d.id, file, step: 'storage', error: 'storage did not confirm the removal of the file' }); continue }
    }
    const { error: rowError, count } = await supabase.from('documents').delete({ count: 'exact' }).eq('id', d.id)
    if (rowError) { failed.push({ id: d.id, file, step: 'document_row', error: rowError.message }); continue }
    if (count !== 1) { failed.push({ id: d.id, file, step: 'document_row', error: `the document record was not removed (rows affected: ${count})` }); continue }
    removed++
  }
  return { ok: failed.length === 0, found: docs.length, removed, failed }
}

// Deletes the record in `table` and, ONLY if the database confirms that exactly this record was deleted,
// removes its documents. Never throws; returns { status } = deleted | db_error | not_deleted | unverified | deleted_documents_failed.
export async function deleteRecordThenDocuments(table, id) {
  const { error, count } = await supabase.from(table).delete({ count: 'exact' }).eq('id', id)
  if (error) return { status: 'db_error', error: error.message }
  if (typeof count !== 'number') return { status: 'unverified' }   // deletion could not be verified: never touch the documents
  if (count === 0) return { status: 'not_deleted' }                // nothing was deleted (no permission / already gone)
  const documents = await removeEntityDocuments(id)
  if (documents.ok) return { status: 'deleted', documents }
  console.error('[delete] record deleted but its documents were not fully removed', { table, id, ...documents })
  return { status: 'deleted_documents_failed', id, documents }
}

// User-facing message for a deleteRecordThenDocuments() result (null when everything succeeded).
export function describeDeleteResult(result, label = 'السجل') {
  switch (result?.status) {
    case 'deleted': return null
    case 'db_error': return `تعذّر حذف ${label}، ولم يتم حذف أي مستند.\nالسبب: ${result.error}`
    case 'not_deleted': return `لم يتم حذف ${label}: لم يتأثر أي سجل (غالباً لا تملك صلاحية الحذف أو السجل لم يعد موجوداً). لم يتم حذف أي مستند.`
    case 'unverified': return `تعذّر التأكد من حذف ${label}، لذلك لم يتم حذف أي مستند. حدّث الصفحة وتأكد من حالة السجل.`
    case 'deleted_documents_failed': {
      const d = result.documents || {}
      if (d.step === 'list') return `تم حذف ${label}، لكن تعذّر قراءة قائمة مستنداته فلم يتم حذف أي مستند.\nالسبب: ${d.error}\nما زالت محفوظة في Document Center، ويمكن إعادة المحاولة بأمان لاحقاً أو إبلاغ الأدمن.`
      const stepText = (s) => s === 'storage' ? 'تعذّر حذف الملف من التخزين (حذف ملفات المستندات بعد حذف السجل يحتاج صلاحية Documents.Delete)' : 'الملف اتحذف لكن تعذّر حذف سجله'
      const lines = (d.failed || []).map(f => `• ${f.file}: ${stepText(f.step)} — ${f.error}`).join('\n')
      return `تم حذف ${label}، لكن حذف مستنداته المرفقة لم يكتمل: اتحذف ${d.removed || 0} من ${d.found || 0}.\nالمستندات التي لم تُحذف:\n${lines}\nما زالت محفوظة بمساراتها في Document Center، ويمكن إعادة المحاولة بأمان لاحقاً (بمن لديه صلاحية Documents.Delete) أو إبلاغ الأدمن.`
    }
    default: return `تعذّر إتمام الحذف (${result?.status || 'unknown'}).`
  }
}

// ── Storage usage indicator ──────────────────────────────────────
export function StorageIndicator() {
  const [used, setUsed] = useState(null)

  useEffect(() => {
    supabase.from('documents')
      .select('file_size')
      .then(({ data }) => {
        const total = (data || []).reduce((s, r) => s + (r.file_size || 0), 0)
        setUsed(total)
      })
  }, [])

  if (used === null) return null

  const MAX     = 500 * 1024 * 1024  // 500 MB
  const pct     = Math.min((used / MAX) * 100, 100)
  const usedMB  = (used / 1048576).toFixed(1)
  const color   = pct > 80 ? '#A32D2D' : pct > 50 ? '#B06D00' : '#1A7A4A'

  return (
    <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#F5F1EA',border:'1px solid #DDD9D0',borderRadius:8}}>
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} style={{width:13,height:13,flexShrink:0}}>
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <div style={{flex:1,minWidth:80}}>
        <div style={{height:4,background:'#DDD9D0',borderRadius:2,overflow:'hidden',marginBottom:2}}>
          <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:2,transition:'width .3s'}}/>
        </div>
        <p style={{fontSize:9,color:'#9A948C',fontWeight:600}}>{usedMB} MB / 500 MB</p>
      </div>
    </div>
  )
}

const fmtSize = b => b > 1048576 ? (b/1048576).toFixed(1)+' MB' : b > 1024 ? (b/1024).toFixed(0)+' KB' : b+' B'
const fmtDate = d => new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})

// ── Small 📎 trigger button ──────────────────────────────────────
export function DocButton({ entityId, entityType, projectId, count: countProp }) {
  const [count,      setCount]      = useState(countProp ?? 0)
  const [open,       setOpen]       = useState(false)

  // Sync when parent-provided batch count changes
  useEffect(() => {
    if (countProp !== undefined) setCount(countProp)
  }, [countProp])

  useEffect(() => {
    // Skip individual query entirely when a batch count was supplied by the parent
    if (countProp !== undefined) return
    if (!entityId) return
    supabase.from('documents').select('id',{count:'exact',head:true})
      .eq('entity_id', entityId)
      .then(({ count: c }) => setCount(c || 0))
  }, [entityId, countProp])

  return (
    <>
      <button
        onClick={e => { e.stopPropagation(); setOpen(true) }}
        title="Documents"
        style={{
          position:'relative', display:'inline-flex', alignItems:'center', justifyContent:'center',
          width:28, height:28, borderRadius:6, border:'1px solid #DDD9D0',
          background: count > 0 ? '#f0fdf4' : '#fff',
          color: count > 0 ? '#1A7A4A' : '#9A948C',
          cursor:'pointer', flexShrink:0
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{width:13,height:13}}>
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        {count > 0 && (
          <span style={{position:'absolute',top:-4,right:-4,background:'#1A7A4A',color:'#fff',borderRadius:10,fontSize:8,fontWeight:700,padding:'0 4px',minWidth:14,textAlign:'center',lineHeight:'14px'}}>
            {count}
          </span>
        )}
      </button>

      {open && (
        <DocumentPanel
          entityId={entityId}
          entityType={entityType}
          projectId={projectId}
          onClose={() => { setOpen(false); supabase.from('documents').select('id',{count:'exact',head:true}).eq('entity_id',entityId).then(({count:c})=>setCount(c||0)) }}
        />
      )}
    </>
  )
}

// ── Full Panel (modal) ───────────────────────────────────────────
function DocumentPanel({ entityId, entityType, projectId, onClose }) {
  const [docs,       setDocs]       = useState([])
  const [loading,    setLoading]    = useState(true)
  const [uploading,  setUploading]  = useState(false)
  const [desc,       setDesc]       = useState('')
  const [dragOver,   setDragOver]   = useState(false)
  const [msg,        setMsg]        = useState(null)
  const fileRef = useRef()

  useEffect(() => { loadDocs() }, [])

  async function loadDocs() {
    setLoading(true)
    const { data } = await supabase.from('documents')
      .select('*')
      .eq('entity_id', entityId)
      .order('uploaded_at', { ascending: false })
    setDocs(data || [])
    setLoading(false)
  }

  async function uploadFile(file) {
    if (!file) return
    setUploading(true); setMsg(null)
    try {
      const ext  = file.name.split('.').pop()
      const path = `${entityType}/${entityId}/${Date.now()}_${file.name.replace(/\s+/g,'_')}`

      const { error: upErr } = await supabase.storage.from('documents').upload(path, file)
      if (upErr) throw upErr

      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(path)

      const { error: dbErr } = await supabase.from('documents').insert({
        project_id:  projectId,
        entity_type: entityType,
        entity_id:   entityId,
        file_name:   file.name,
        file_path:   path,
        file_size:   file.size,
        mime_type:   file.type,
        description: desc || null,
      })
      if (dbErr) throw dbErr

      setDesc('')
      setMsg({ type:'success', text:'✓ Uploaded successfully' })
      loadDocs()
    } catch(err) {
      setMsg({ type:'error', text: 'Upload failed: ' + err.message })
    }
    setUploading(false)
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.file_name}"?`)) return
    await supabase.storage.from('documents').remove([doc.file_path])
    await supabase.from('documents').delete().eq('id', doc.id)
    loadDocs()
  }

  async function handleOpen(doc) {
    const { data } = await supabase.storage.from('documents').createSignedUrl(doc.file_path, 3600)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  function handleDrop(e) {
    e.preventDefault(); setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) uploadFile(file)
  }

  // NOTE: was a local `typeLabel` object here missing 'equipment_ipc' and 'equipment_contract'
  // (documents for those would fall back to the raw entityType string). Now reads from the
  // single shared source (lib/documentTypes.js) — see entity_type unification sprint.

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:1000}}/>

      {/* Panel */}
      <div style={{
        position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
        width:500, maxHeight:'80vh', background:'#fff', borderRadius:16,
        border:'1px solid #DDD9D0', boxShadow:'0 20px 60px rgba(0,0,0,0.15)',
        zIndex:1001, display:'flex', flexDirection:'column', overflow:'hidden'
      }}>

        {/* Header */}
        <div style={{padding:'16px 20px',borderBottom:'1px solid #EDE7DA',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
          <div>
            <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:2}}>{getDocumentTypeConfig(entityType).label}</p>
            <p style={{fontSize:14,fontWeight:700,color:'#1C1A18'}}>Documents</p>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#9A948C',fontSize:18,lineHeight:1}}>✕</button>
        </div>

        {/* Body */}
        <div style={{flex:1,overflowY:'auto',padding:'16px 20px',display:'flex',flexDirection:'column',gap:12}}>

          {msg && (
            <div style={{padding:'8px 12px',borderRadius:8,background:msg.type==='success'?'#f0fdf4':'#fef2f2',border:`1px solid ${msg.type==='success'?'#bbf7d0':'#fecaca'}`,fontSize:11,color:msg.type==='success'?'#15803d':'#991b1b',display:'flex',justifyContent:'space-between'}}>
              <span>{msg.text}</span>
              <button onClick={()=>setMsg(null)} style={{background:'none',border:'none',cursor:'pointer',color:'inherit'}}>✕</button>
            </div>
          )}

          {/* Upload area */}
          <div
            onDragOver={e=>{e.preventDefault();setDragOver(true)}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border:`2px dashed ${dragOver?'#1A7A4A':'#DDD9D0'}`,
              borderRadius:10, padding:'20px 16px', textAlign:'center',
              background:dragOver?'#f0fdf4':'#FDFCFA', transition:'all .15s', cursor:'pointer'
            }}
            onClick={()=>fileRef.current?.click()}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke={dragOver?'#1A7A4A':'#9A948C'} strokeWidth={1.5} style={{width:28,height:28,margin:'0 auto 8px'}}>
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <p style={{fontSize:12,fontWeight:600,color:dragOver?'#1A7A4A':'#6B655C',marginBottom:4}}>
              {uploading ? 'Uploading…' : 'Drop file here or click to browse'}
            </p>
            <p style={{fontSize:10,color:'#9A948C'}}>PDF, JPG, PNG · Max 50MB</p>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
              onChange={e=>{ if(e.target.files[0]) uploadFile(e.target.files[0]); e.target.value='' }}
              style={{display:'none'}}/>
          </div>

          {/* Description input */}
          <input type="text" placeholder="Description (optional) — e.g. Original Contract, Addendum 1, Bank Guarantee"
            value={desc} onChange={e=>setDesc(e.target.value)}
            style={{padding:'8px 12px',border:'1px solid #DDD9D0',borderRadius:8,fontSize:12,outline:'none',color:'#1C1A18',background:'#FDFCFA'}}/>

          {/* Files list */}
          {loading ? (
            <div style={{textAlign:'center',padding:20,color:'#9A948C',fontSize:12}}>Loading…</div>
          ) : docs.length === 0 ? (
            <div style={{textAlign:'center',padding:20,color:'#9A948C',fontSize:12}}>No documents yet</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {docs.map(doc=>(
                <div key={doc.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'#F5F1EA',borderRadius:8,border:'1px solid #EDE7DA'}}>
                  {/* Icon */}
                  <div style={{width:32,height:32,borderRadius:6,background:'#fff',border:'1px solid #DDD9D0',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    {doc.mime_type?.includes('pdf') ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="#A32D2D" strokeWidth={2} style={{width:14,height:14}}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="#1A7A4A" strokeWidth={2} style={{width:14,height:14}}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    )}
                  </div>
                  {/* Info */}
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontSize:12,fontWeight:600,color:'#1C1A18',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{doc.file_name}</p>
                    <p style={{fontSize:10,color:'#9A948C',marginTop:1}}>
                      {doc.description && <span style={{color:'#6B655C',marginRight:6}}>{doc.description} ·</span>}
                      {doc.file_size && fmtSize(doc.file_size)} · {fmtDate(doc.uploaded_at)}
                    </p>
                  </div>
                  {/* Actions */}
                  <div style={{display:'flex',gap:4,flexShrink:0}}>
                    <button onClick={()=>handleOpen(doc)} title="Open"
                      style={{width:26,height:26,borderRadius:5,border:'1px solid #DDD9D0',background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#1A7A4A'}}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{width:12,height:12}}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <button onClick={()=>handleDelete(doc)} title="Delete"
                      style={{width:26,height:26,borderRadius:5,border:'1px solid #fecaca',background:'#fef2f2',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#A32D2D'}}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{width:12,height:12}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
